import { useState, useEffect } from 'react'

/**
 * Hash map of race condition IDs.
 * Key is hook ID
 * Value is latest request ID.
 * Response of the request will be processed only if request ID is matching
 */
const raceConditionRegistry = {}

/**
 * Function to get hookId for request
 * It prevents race conditions for the same requests,
 * but in some application places we could have components
 * which made requests in the same time.
 * So, we need to generate request depends on it fetch function name and
 * arguments. To prevent blocking requests happened in the same time, but
 * for different subjects.
 */
const getHookId = (fetchId, args) => {
  let hookId = `hook-${Date.now()}-`

  hookId += fetchId ? fetchId : 'undefinedFetchId'

  if (args) {
    try {
      hookId += '-' + JSON.stringify(args)
    } catch (e) {
      console.error(e)
    }
  }

  return hookId
}

/**
 * @typedef UseFetchResponse
 * @property {*} data Result of the response.
 * If provided, `args.defaultData` is set before fetch response is received
 * @property {function} refetch Trigger data refetch.
 * Argument form this function is passed to `fetchFn`
 * @property {boolean} isLoading Is request is on progress
 * @property {boolean} isLoaded Has loaded data
 * @property {boolean} isError Has error occurred
 * @property {string} errorMessage Error message if error occurred
 */

/**
 * React hook to fetch data from the endpoint
 * @param {Object} args
 * @param {*} args.defaultFetchArgs arguments, passed to `fetchFn` on hook initialization
 * @param {function} args.fetchFn Required. Function to fetch data from the remote endpoint.
 * It has to return fetch-based promise.
 * When this function is called on hook initialization - it will receive `defaultFetchArgs` from the hook arguments
 * When `refetch` is called - this function will be called and will receive arguments from refetch
 * @param {function} mapResponse Function that maps response data before saving in state
 * @param {function} validateResult Function that receive JSON result of the response for validation.
 * It is expected that this function return array with following structure:
 * [isValid: boolean, errorMessage: string|null]
 * @param {boolean} skipFetchOnInit Do not fetch data when hook is initialized. Defaults to false
 * @param {*} defaultData Value that is set to `data` before fetch response is received.
 * It is set to data when hook is initialized and when `refetch` is called. Defaults to nul
 * @param {function} args.fetchId Optional. Id to use in hook id to prevent fetching the same request
 *
 * @returns {UseFetchResponse}
 */
const useFetch = ({
  fetchFn,
  defaultFetchArgs,
  mapResponse,
  validateResult,
  skipFetchOnInit = false,
  defaultData = null,
  fetchId,
} = {}) => {
  const [hookId] = useState(getHookId(fetchId, defaultFetchArgs))
  const [isLoading, setIsLoading] = useState(false)
  const [isLoaded, setIsLoaded] = useState(false)
  const [isError, setIsError] = useState(false)
  const [errorMessage, setErrorMessage] = useState(null)
  const [data, setData] = useState(defaultData)

  const fetchData = async args => {
    if (typeof fetchFn !== 'function') {
      console.warn('"useFetch" hook used with no "fetchFn" provided')
      return
    }

    setIsLoading(true)
    setIsLoaded(false)
    setIsError(false)
    setErrorMessage(null)
    setData(defaultData)
    try {
      const savedRequestId = Date.now()
      raceConditionRegistry[hookId] = savedRequestId

      const response = await fetchFn(args)

      if (raceConditionRegistry[hookId] !== savedRequestId) {
        //Race condition occurred
        return
      }

      setIsLoading(false)

      if (!response.ok) {
        setIsError(true)
        setErrorMessage('Failed to fetch')
      }
      const result = await response.json()

      if (typeof validateResult === 'function') {
        const validationResult = validateResult(result)
        if (Array.isArray(validationResult)) {
          const [isValid, errorMessage] = validationResult
          if (isValid === false) {
            setIsError(true)
            setErrorMessage(errorMessage)
            return
          }
        }
      }

      const mappedData =
        typeof mapResponse === 'function' ? mapResponse(result) : result

      setData(mappedData)
      setIsLoaded(true)
    } catch (error) {
      setIsError(true)
      setErrorMessage(error.message)
      setIsLoaded(true)
    }
  }

  useEffect(() => {
    if (!skipFetchOnInit) fetchData(defaultFetchArgs)
  }, [])

  return {
    data,
    refetch: fetchData,
    isLoading,
    isLoaded,
    isError,
    errorMessage,
  }
}

export default useFetch



// WEBPACK FOOTER //
// ./src/Utils/Hooks/useFetch.js